/** @jsxImportSource @emotion/react */
import React from "react";
import {
    GBVFormRow, GBVFormGroup, GBVLabel, GBVRequired, GBVInput, GBVSelect, GBVTextarea,


    InjuryCard, InjuryHeader, InjuryNumber, InjuryRemoveBtn, AddInjuryBtn,

    GBVAlertBanner, GBVAlertIcon, GBVAlertContent, GBVAlertTitle, GBVAlertDetail,
    GBVActionBar, GBVPrimaryBtn, GBVSecondaryBtn,
    GBVFieldError,
    ConsentDialogOverlay, ConsentDialogBox, ConsentDialogTitle, ConsentDialogText, ConsentDialogActions,
} from "./GBVScreeningStyles";

import {
    GBV_TYPES,
    INJURY_LOCATIONS,
    INJURY_TYPES,
} from "./hooks/useGBVAssessment";
import { ResizableTextarea } from "../../../Utils/FormInputs/Textarea";
import Form from 'react-bootstrap/Form';
import Label from "../../../Utils/FormInputs/Label";
import SelectInput from "../../../Utils/FormInputs/SelectInput";
import { ErrorMessage } from "../../../Utils/styledComponents";
import TextField from "../../../Utils/FormInputs/TextField";

/* ═══════════════════════════════════════════════════════════════
   GBVAssessmentForm — Confidential GBV assessment form (Task 20).
   Sections:
     1. Consent & Confidentiality
     2. Screening Information (date, type of violence)
     3. Disclosure Details
     4. Injury Documentation
     5. Clinical Management Checklist
     6. Referral Tracking
     7. PF3 Form (Police Medical Form)
     8. Safety Plan
     9. Follow-up
   ═══════════════════════════════════════════════════════════════ */

const formatDate = function (dateStr) {
    if (!dateStr) return "—";
    try {
        const d = new Date(dateStr);
        return d.toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" });
    } catch (e) {
        return dateStr;
    }
};

export const GBVAssessmentForm = function (props) {
    const gbv = props.gbv;
    const form = gbv.form;
    const errors = gbv.validationErrors;

    const onChange = function (field) {
        return function (e) {
            gbv.handleChange(field, e.target.value);
        };
    };

    const onBoolChange = function (field) {
        return function () {
            gbv.handleChange(field, !form[field]);
        };
    };

    return (
        <div>
                    <GBVFormRow cols="1fr 1fr">
                        <Form.Group>
                            <Label name='Assessment Date' type/>
                               
                            <TextField
                                type="date"
                                value={form.screening_date}
                                onChange={onChange("screening_date")}
                            />
                            {gbv.hasAttemptedSubmit && errors.screening_date && (
                                <ErrorMessage>{errors.screening_date}</ErrorMessage>
                            )}
                        </Form.Group>
                        <Form.Group>
                            <Label name='Follow-up Date'/>
                               
                            <TextField
                                type="date"
                                value={form.follow_up_date}
                                onChange={onChange("follow_up_date")}
                            />
                        </Form.Group>
                    </GBVFormRow>

                    <Form.Group>
                        <Label name='Type of Violence' type/>
                           
                        <SelectInput
                            value={form.gbv_type}
                            onChange={onChange("gbv_type")}
                            defaultOption='— Select Type of Violence —'
                            options={GBV_TYPES.map(type=>({
                                value:type.value, text:type.label
                            }))}
                        />
                           
                        {gbv.hasAttemptedSubmit && errors.gbv_type && (
                            <ErrorMessage>{errors.gbv_type}</ErrorMessage>
                        )}
                    </Form.Group>

                    <Form.Group>
                        <Label name='Patient&apos;s Account' type/>
                          
                        <ResizableTextarea
                            value={form.disclosure_details}
                            rows="2"
                            onChange={onChange("disclosure_details")}
                            placeholder="Document the patient's account in their own words. Include: what happened, when, where, who was the perpetrator (relationship), and any weapons used. Document factually without interpretation."
                        />
                        {gbv.hasAttemptedSubmit && errors.disclosure_details && (
                            <GBVFieldError>{errors.disclosure_details}</GBVFieldError>
                        )}
                    </Form.Group>

                    <Form.Group>
                        <Label name='Consent Obtained' type/>
                          
                        <div style={{ display: 'flex', gap: '20px', alignItems: 'center', marginTop: '8px' }}>
                            <Form.Check
                                type="radio"
                                label="Yes"
                                name="consent_obtained"
                                id="consent_yes"
                                checked={form.consent_obtained === true}
                                onChange={function () { gbv.handleChange("consent_obtained", true); }}
                            />
                            <Form.Check
                                type="radio"
                                label="No"
                                name="consent_obtained"
                                id="consent_no"
                                checked={form.consent_obtained === false}
                                onChange={function () { gbv.handleChange("consent_obtained", false); }}
                            />
                        </div>
                        {gbv.hasAttemptedSubmit && errors.consent_obtained && (
                            <GBVFieldError>{errors.consent_obtained}</GBVFieldError>
                        )}
                    </Form.Group>

                    {form.injuries_documented.length === 0 && (
                        <p style={{ fontSize: "0.82rem", color: "#888", marginBottom: "12px" }}>
                            No injuries documented yet. Click below to add injury details.
                        </p>
                    )}

                    {form.injuries_documented.map(function (injury, idx) {
                        return (
                            <InjuryCard key={idx}>
                                <InjuryHeader>
                                    <InjuryNumber>Injury #{idx + 1}</InjuryNumber>
                                    <InjuryRemoveBtn
                                        onClick={function () { gbv.removeInjury(idx); }}
                                        type="button"
                                    >
                                        Remove
                                    </InjuryRemoveBtn>
                                </InjuryHeader>
                                <GBVFormRow cols="1fr 1fr 1fr">
                                    <Form.Group>
                                        <Label name='Location'/>
                                        <SelectInput
                                            value={injury.location}
                                            onChange={function (e) { gbv.updateInjury(idx, "location", e.target.value); }}
                                            defaultOption='— Select Injury Location —'
                                            options={INJURY_LOCATIONS.map(function (loc) {
                                                return { value: loc.value, text: loc.label };
                                            })}
                                        />
                                    </Form.Group>
                                    <Form.Group>
                                        <Label name='Type'/>
                                        <SelectInput
                                            value={injury.type}
                                            onChange={function (e) { gbv.updateInjury(idx, "type", e.target.value); }}
                                            defaultOption='— Select Type —'
                                            options={INJURY_TYPES.map(function (t) {
                                                return { value: t.value, text: t.label };
                                            })}
                                        />
                                    </Form.Group>
                                    <Form.Group>
                                        <Label name='Type'/>
                                        <SelectInput
                                            value={injury.type}
                                            onChange={function (e) { gbv.updateInjury(idx, "type", e.target.value); }}
                                            defaultOption='— Select Type —'
                                            options={INJURY_TYPES.map(function (t) {
                                                return { value: t.value, text: t.label };
                                            })}
                                        />
                                    </Form.Group>
                                    <Form.Group>
                                        <Label name='Description'/>
                                        <TextInput
                                            value={injury.description}
                                            onChange={function (e) { gbv.updateInjury(idx, "description", e.target.value); }}
                                            placeholder="Size, colour, pattern..."
                                        />
                                    </Form.Group>
                                </GBVFormRow>
                            </InjuryCard>
                        );
                    })}

                    <AddInjuryBtn onClick={gbv.addInjury} type="button">
                        Add Injury Documentation
                    </AddInjuryBtn>

                    <Form.Group className="mt-3">
                        <Form.Label>Clinical Interventions</Form.Label>
                        <ResizableTextarea
                            value={form.clinical_management}
                            rows="2"
                            onChange={onChange("clinical_management")}
                            placeholder="Document all clinical interventions provided (e.g. wound care, STI prophylaxis, EC, HIV PEP, Hep B vaccine, tetanus)..."
                        />
                    </Form.Group>


                    <Form.Group>
                        <Form.Label>Psychosocial First Aid / Counselling Provided</Form.Label>
                        <div style={{ fontSize: "0.82rem", color: "#666", marginBottom: "8px" }}>
                            First-line emotional support, active listening, and psychological first aid
                        </div>
                        <div style={{ display: 'flex', gap: '20px' }}>
                            <Form.Check
                                type="radio"
                                label="Yes"
                                name="psychosocial_support_given"
                                id="ps_yes"
                                checked={form.psychosocial_support_given === true}
                                onChange={function() { gbv.handleChange("psychosocial_support_given", true); }}
                            />
                            <Form.Check
                                type="radio"
                                label="No"
                                name="psychosocial_support_given"
                                id="ps_no"
                                checked={form.psychosocial_support_given === false}
                                onChange={function() { gbv.handleChange("psychosocial_support_given", false); }}
                            />
                        </div>
                    </Form.Group>

                    <Form.Group>
                        <Form.Label>Referral Type</Form.Label>
                        <SelectInput
                            value={form.referral_type}
                            onChange={onChange("referral_type")}
                            defaultOption="— Select Referral Type —"
                            options={[
                                { value: "", text: "— None —" },
                                { value: "police", text: "Police / Law Enforcement" },
                                { value: "safe_shelter", text: "Safe Shelter / Safe House" },
                                { value: "legal_aid", text: "Legal Aid / FIDA" },
                                { value: "psychosocial", text: "Psychosocial Support" }
                            ]}
                        />
                    </Form.Group>

                      <Form.Group>
                        <Form.Label>Psychosocial Referral Details</Form.Label>
                        <ResizableTextarea
                            value={form.psychosocial_referral}
                            onChange={onChange("psychosocial_referral")}
                            placeholder="Referral organisation, counsellor name, appointment date, contact details..."
                            rows="2"
                        />
                    </Form.Group>

                    <Form.Group>
                        <Form.Label>PF3 Form Completed (Police Medical Examination Form)</Form.Label>
                        <div style={{ display: 'flex', gap: '20px' }}>
                            <Form.Check
                                type="radio"
                                label="Yes"
                                name="pf3_completed"
                                id="pf3_yes"
                                checked={form.pf3_completed === true}
                                onChange={function() { gbv.handleChange("pf3_completed", true); }}
                            />
                            <Form.Check
                                type="radio"
                                label="No"
                                name="pf3_completed"
                                id="pf3_no"
                                checked={form.pf3_completed === false}
                                onChange={function() { gbv.handleChange("pf3_completed", false); }}
                            />
                        </div>
                    </Form.Group>

                    {form.referral_type === "police" && !form.pf3_completed && (
                        <GBVAlertBanner type="warning">
                            <GBVAlertContent>
                                <GBVAlertTitle>PF3 Recommended</GBVAlertTitle>
                                <GBVAlertDetail>
                                    Police referral has been made but PF3 form has not been completed.
                                    The PF3 is essential for legal proceedings.
                                </GBVAlertDetail>
                            </GBVAlertContent>
                        </GBVAlertBanner>
                    )}

            {/* ═══ Action Bar ═══ */}
            <GBVActionBar>
                {gbv.editingRecord && (
                    <GBVSecondaryBtn onClick={gbv.cancelEdit} type="button">
                        Cancel Edit
                    </GBVSecondaryBtn>
                )}
                <GBVSecondaryBtn onClick={gbv.resetForm} type="button">
                    Reset Form
                </GBVSecondaryBtn>
                <button className="sms-btn btn"
                    onClick={gbv.handleSubmit}
                    disabled={gbv.submitting}
                    type="button"
                >
                    {gbv.submitting
                        ? "Saving..."
                        : gbv.editingRecord
                            ? "Update Assessment"
                            : "Save Confidential Assessment"}
                </button>
            </GBVActionBar>
            {gbv.showConsentDialog && (
                <ConsentDialogOverlay>
                    <ConsentDialogBox>
                        <ConsentDialogTitle>Confirm Confidential Consent</ConsentDialogTitle>
                        <ConsentDialogText>
                            Patient consent must be obtained before documenting confidential GBV details.
                            Click Confirm to mark consent as obtained and continue saving the assessment, or Cancel to return to the form.
                        </ConsentDialogText>
                        <ConsentDialogActions>
                            <button
                                type="button"
                                className="sms-btn btn btn-secondary"
                                onClick={function () { gbv.setShowConsentDialog(false); }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="sms-btn btn btn-primary"
                                onClick={function () {
                                    gbv.handleSubmitConfirmed();
                                }}
                            >
                                Confirm Consent & Save
                            </button>
                        </ConsentDialogActions>
                    </ConsentDialogBox>
                </ConsentDialogOverlay>
            )}
        </div>
    );
};
